/* RyanTech effects: theme toggle, pixel-particle hero, card spotlight, terminal typer,
   eyebrow scramble and CTA pixel rain. No dependencies; everything idles when offscreen. */
(() => {
  const root = document.documentElement;
  const STORAGE_KEY = 'rt-theme';
  const systemDark = window.matchMedia('(prefers-color-scheme: dark)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------------- Theme ---------------- */

  const readStored = () => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return null;
    }
  };
  const writeStored = (value) => {
    try {
      if (value) localStorage.setItem(STORAGE_KEY, value);
      else localStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
  };

  const syncToggles = () => {
    const dark = root.dataset.theme === 'dark';
    document.querySelectorAll('.rt-theme-toggle').forEach((btn) => {
      btn.setAttribute('aria-pressed', String(dark));
      btn.setAttribute('aria-label', btn.dataset[dark ? 'labelLight' : 'labelDark'] || 'Toggle theme');
    });
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', dark ? '#141517' : '#f7f8f5');
  };

  const applyTheme = (theme, origin) => {
    if (root.dataset.theme === theme) return;
    const swap = () => {
      root.dataset.theme = theme;
      syncToggles();
    };
    if (reducedMotion.matches) return swap();
    if (document.startViewTransition && origin) {
      const r = origin.getBoundingClientRect();
      const x = r.left + r.width / 2;
      const y = r.top + r.height / 2;
      const radius = Math.hypot(Math.max(x, innerWidth - x), Math.max(y, innerHeight - y));
      root.style.setProperty('--rt-wipe-x', `${x}px`);
      root.style.setProperty('--rt-wipe-y', `${y}px`);
      root.style.setProperty('--rt-wipe-r', `${radius}px`);
      document.startViewTransition(swap);
      return;
    }
    root.classList.add('rt-theme-anim');
    swap();
    setTimeout(() => root.classList.remove('rt-theme-anim'), 400);
  };

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('.rt-theme-toggle');
    if (!btn) return;
    const next = root.dataset.theme === 'dark' ? 'light' : 'dark';
    // Choosing the same theme as the OS hands control back to auto-detect.
    writeStored(next === (systemDark.matches ? 'dark' : 'light') ? null : next);
    applyTheme(next, btn);
  });

  systemDark.addEventListener('change', (e) => {
    if (!readStored()) applyTheme(e.matches ? 'dark' : 'light');
  });

  syncToggles();

  /* ---------------- Helpers ---------------- */

  const SLANT = 0.2125; // RT mark tile slant
  const css = (name) => getComputedStyle(root).getPropertyValue(name).trim();
  const hexToRgb = (hex) => {
    const h = hex.replace('#', '');
    const n = parseInt(h.length === 3 ? h.replace(/./g, '$&$&') : h, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  };
  const mixCache = new Map();
  const mix = (a, b, t) => {
    const step = Math.max(0, Math.min(12, Math.round(t * 12)));
    const key = `${a}|${b}|${step}`;
    let out = mixCache.get(key);
    if (!out) {
      out = `rgb(${a.map((v, i) => Math.round(v + (b[i] - v) * (step / 12))).join(',')})`;
      mixCache.set(key, out);
    }
    return out;
  };
  const onIdle = (fn) => ('requestIdleCallback' in window ? requestIdleCallback(fn, { timeout: 1200 }) : setTimeout(fn, 200));

  // Run `tick` in rAF only while `el` is on screen and the tab is visible.
  const loopWhileVisible = (el, tick) => {
    let raf = 0;
    let visible = false;
    const frame = (t) => {
      raf = 0;
      if (tick(t) !== false) schedule();
    };
    const schedule = () => {
      if (!raf && visible && !document.hidden) raf = requestAnimationFrame(frame);
    };
    new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      schedule();
    }).observe(el);
    document.addEventListener('visibilitychange', schedule);
    return { wake: schedule };
  };

  const tilePath = (ctx, x, y, s) => {
    const o = s * SLANT;
    ctx.moveTo(x + o, y);
    ctx.lineTo(x + o + s, y);
    ctx.lineTo(x + s, y + s);
    ctx.lineTo(x, y + s);
    ctx.closePath();
  };

  const fitCanvas = (canvas) => {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    return { ctx, w, h };
  };

  /* ---------------- Pixel-particle hero ---------------- */

  // The RT mark, cell for cell (1 = mint, 2 = pine), from mark/ryantech-mark-primary-*.svg
  const MARK = [
    '111111..111111',
    '1111111.111111',
    '11...11...11..',
    '11...11...12..',
    '1111111...22..',
    '111222....22..',
    '22.22.....22..',
    '22..22....22..',
    '22...22...22..',
  ];
  const MARK_W = 157.7; // viewBox of the mark SVG
  const MARK_H = 88.8;

  const initHero = (section) => {
    const canvas = section.querySelector('.rt-hero__canvas');
    const stage = section.querySelector('.rt-hero__stage');
    if (!canvas || !stage) return;

    let ctx;
    let W = 0;
    let H = 0;
    let colors;
    const tiles = [];
    const dust = [];
    const pointer = { x: -1e4, y: -1e4, active: false };
    const density = Number(section.dataset.density || 1);
    let started = 0;

    MARK.forEach((row, r) => {
      [...row].forEach((ch, c) => {
        if (ch === '.') return;
        tiles.push({ c, r, tone: ch === '1' ? 'mint' : 'pine', x: 0, y: 0, vx: 0, vy: 0, tx: 0, ty: 0, flash: 0, delay: 0 });
      });
    });

    const readColors = () => {
      colors = {
        mint: hexToRgb(css('--rt-mint') || '#2cce82'),
        pine: hexToRgb(css('--rt-pine') || '#19754d'),
        amber: hexToRgb(css('--rt-amber') || '#f5b83d'),
        hot: root.dataset.theme === 'dark' ? [236, 238, 234] : [255, 255, 255],
        dark: root.dataset.theme === 'dark',
      };
    };

    let size = 10;
    const layout = (scatter) => {
      ({ ctx, w: W, h: H } = fitCanvas(canvas));
      const cr = canvas.getBoundingClientRect();
      const sr = stage.getBoundingClientRect();
      const scale = Math.min(sr.width / MARK_W, sr.height / MARK_H);
      const ox = sr.left - cr.left + (sr.width - MARK_W * scale) / 2;
      const oy = sr.top - cr.top + (sr.height - MARK_H * scale) / 2;
      size = 8.8 * scale;
      tiles.forEach((t) => {
        // top-left of the skewed cell's bounding box, same math as the brand SVG
        t.tx = ox + (t.c * 10 + (MARK_H - t.r * 10 - 8.8) * SLANT) * scale;
        t.ty = oy + t.r * 10 * scale;
        if (scatter) {
          t.x = Math.random() * W;
          t.y = H + Math.random() * H * 0.6;
          t.delay = t.c * 45 + Math.random() * 260;
        }
      });
      const want = Math.round(((W * H) / 14000) * density);
      while (dust.length < want) dust.push(makeDust(true));
      dust.length = Math.min(dust.length, want);
    };

    const makeDust = (anywhere) => ({
      x: Math.random() * W,
      y: anywhere ? Math.random() * H : H + 10,
      s: 3 + Math.random() * 5,
      v: 0.08 + Math.random() * 0.3,
      a: 0.08 + Math.random() * 0.22,
      tone: Math.random() < 0.5 ? 'mint' : 'pine',
      spark: Math.random() < 0.04,
      glow: 0,
      ox: 0,
      oy: 0,
    });

    const draw = (now, animate) => {
      ctx.clearRect(0, 0, W, H);
      const R = Math.max(90, size * 5);

      // ambient dust
      for (const d of dust) {
        if (animate) {
          d.y -= d.v;
          if (d.y < -10) Object.assign(d, makeDust(false));
          const dx = d.x + d.ox - pointer.x;
          const dy = d.y + d.oy - pointer.y;
          const dist = Math.hypot(dx, dy);
          if (pointer.active && dist < R * 1.4) {
            const f = (1 - dist / (R * 1.4)) * 2.2;
            d.ox += (dx / (dist || 1)) * f;
            d.oy += (dy / (dist || 1)) * f;
            d.glow = Math.min(1, d.glow + 0.15);
          }
          d.ox *= 0.94;
          d.oy *= 0.94;
          d.glow *= 0.96;
        }
        const base = d.spark ? colors.amber : colors[d.tone];
        ctx.globalAlpha = Math.min(1, d.a + d.glow * 0.6 + (d.spark ? 0.25 * (1 + Math.sin(now / 300 + d.x)) : 0));
        ctx.fillStyle = mix(base, colors.hot, d.glow * 0.3);
        ctx.beginPath();
        tilePath(ctx, d.x + d.ox, d.y + d.oy, d.s);
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      // mark tiles
      let moving = false;
      const t0 = now - started;
      for (const t of tiles) {
        if (animate) {
          if (t0 > t.delay) {
            t.vx += (t.tx - t.x) * 0.045;
            t.vy += (t.ty - t.y) * 0.045;
          }
          if (pointer.active) {
            const dx = t.x + size / 2 - pointer.x;
            const dy = t.y + size / 2 - pointer.y;
            const dist = Math.hypot(dx, dy);
            if (dist < R) {
              const f = (1 - dist / R) ** 2 * 5;
              t.vx += (dx / (dist || 1)) * f;
              t.vy += (dy / (dist || 1)) * f;
              t.flash = Math.max(t.flash, 1 - dist / R);
            }
          }
          t.vx *= 0.8;
          t.vy *= 0.8;
          t.x += t.vx;
          t.y += t.vy;
          t.flash *= 0.94;
          if (Math.abs(t.vx) + Math.abs(t.vy) > 0.05) moving = true;
        }
        const spin = Math.max(-0.6, Math.min(0.6, t.vx * 0.04));
        ctx.fillStyle = mix(colors[t.tone], colors.hot, t.flash * (colors.dark ? 0.55 : 0.4));
        ctx.beginPath();
        if (Math.abs(spin) < 0.01) {
          tilePath(ctx, t.x, t.y, size); // settled tiles skip the transform
        } else {
          ctx.save();
          ctx.translate(t.x + size / 2, t.y + size / 2);
          ctx.rotate(spin);
          tilePath(ctx, -size / 2, -size / 2, size);
          ctx.restore();
        }
        ctx.fill();
      }
      return moving;
    };

    const shimmer = () => {
      const t = tiles[(Math.random() * tiles.length) | 0];
      t.flash = Math.max(t.flash, 0.7);
    };

    const burst = (x, y) => {
      for (const t of tiles) {
        const dx = t.x - x;
        const dy = t.y - y;
        const dist = Math.hypot(dx, dy) || 1;
        const f = 18 + Math.random() * 22;
        t.vx += (dx / dist) * f + (Math.random() - 0.5) * 10;
        t.vy += (dy / dist) * f + (Math.random() - 0.5) * 10;
        t.flash = 1;
      }
    };

    readColors();

    if (reducedMotion.matches) {
      layout(false);
      tiles.forEach((t) => ((t.x = t.tx), (t.y = t.ty)));
      draw(0, false);
      new ResizeObserver(() => {
        layout(false);
        tiles.forEach((t) => ((t.x = t.tx), (t.y = t.ty)));
        draw(0, false);
      }).observe(section);
      new MutationObserver(() => (readColors(), draw(0, false))).observe(root, { attributeFilter: ['data-theme'] });
      return;
    }

    layout(true);
    started = performance.now();
    let lastShimmer = 0;
    const loop = loopWhileVisible(section, (now) => {
      if (now - lastShimmer > 140) {
        shimmer();
        lastShimmer = now;
      }
      draw(now, true);
    });

    let resizeRaf = 0;
    new ResizeObserver(() => {
      cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(() => layout(false));
    }).observe(section);
    new MutationObserver(readColors).observe(root, { attributeFilter: ['data-theme'] });

    const setPointer = (e) => {
      const r = canvas.getBoundingClientRect();
      pointer.x = e.clientX - r.left;
      pointer.y = e.clientY - r.top;
      pointer.active = true;
      loop.wake();
    };
    section.addEventListener('pointermove', setPointer, { passive: true });
    section.addEventListener('pointerleave', () => (pointer.active = false));
    section.addEventListener('pointerdown', (e) => {
      if (e.target.closest('a, button, .rt-hero__copy')) return; // keep text selectable
      setPointer(e);
      burst(pointer.x, pointer.y);
      if (e.pointerType !== 'mouse') setTimeout(() => (pointer.active = false), 250);
    });
  };

  /* ---------------- Card spotlight ---------------- */

  const initSpotlight = () => {
    document.addEventListener(
      'pointermove',
      (e) => {
        const card = e.target.closest && e.target.closest('.rt-card');
        if (!card) return;
        const r = card.getBoundingClientRect();
        card.style.setProperty('--mx', `${e.clientX - r.left}px`);
        card.style.setProperty('--my', `${e.clientY - r.top}px`);
      },
      { passive: true }
    );
  };

  /* ---------------- Eyebrow scramble ---------------- */

  const GLYPHS = '▖▗▘▙▚▛▜▝▞▟░▒▓█';
  const scramble = (el) => {
    const final = el.textContent;
    const len = final.length;
    const t0 = performance.now();
    const dur = 700;
    const step = (now) => {
      const p = Math.min(1, (now - t0) / dur);
      const settled = Math.floor(p * len);
      let out = final.slice(0, settled);
      for (let i = settled; i < len; i++) out += final[i] === ' ' ? ' ' : GLYPHS[(Math.random() * GLYPHS.length) | 0];
      el.textContent = out;
      if (p < 1) requestAnimationFrame(step);
      else el.textContent = final;
    };
    requestAnimationFrame(step);
  };
  const initScramble = () => {
    if (reducedMotion.matches) return;
    const els = document.querySelectorAll('[data-rt-scramble]');
    if (!els.length) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);
        scramble(entry.target);
      });
    }, { rootMargin: '0px 0px -10% 0px' });
    els.forEach((el) => io.observe(el));
  };

  /* ---------------- Terminal typer ---------------- */

  const initTerminal = (term) => {
    const body = term.querySelector('.rt-terminal__body');
    const src = term.querySelector('script[type="application/json"]');
    if (!body || !src) return;
    let lines;
    try {
      lines = JSON.parse(src.textContent);
    } catch (e) {
      return;
    }
    const kind = (line) => {
      if (line.startsWith('$ ')) return ['t-c', line.slice(2), true];
      if (line.startsWith('✓') || line.startsWith('+')) return ['t-ok', line, false];
      if (line.startsWith('!')) return ['t-w', line, false];
      return ['t-o', line, false];
    };
    const cursor = document.createElement('span');
    cursor.className = 'rt-terminal__cursor';

    const render = (instant) => {
      body.textContent = '';
      body.append(cursor);
      let i = 0;
      const next = () => {
        if (i >= lines.length) return;
        const [cls, text, typed] = kind(lines[i++]);
        const row = document.createElement('span');
        if (typed) {
          const p = document.createElement('span');
          p.className = 't-p';
          p.textContent = '❯ ';
          row.append(p);
        }
        const span = document.createElement('span');
        span.className = cls;
        row.append(span, '\n');
        body.insertBefore(row, cursor);
        if (instant || !typed) {
          span.textContent = text;
          if (instant) return next();
          return setTimeout(next, 90 + Math.random() * 140);
        }
        let c = 0;
        const type = () => {
          span.textContent = text.slice(0, ++c);
          if (c < text.length) setTimeout(type, 22 + Math.random() * 40);
          else setTimeout(next, 380);
        };
        setTimeout(type, 300);
      };
      next();
    };

    if (reducedMotion.matches) return render(true);
    const io = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      io.disconnect();
      render(false);
    }, { threshold: 0.35 });
    io.observe(term);
  };

  /* ---------------- CTA pixel rain ---------------- */

  const initRain = (canvas) => {
    if (reducedMotion.matches) return;
    let ctx;
    let W;
    let H;
    let drops = [];
    const size = 7;
    const setup = () => {
      ({ ctx, w: W, h: H } = fitCanvas(canvas));
      const cols = Math.floor(W / (size + 5));
      drops = Array.from({ length: cols }, (_, i) => ({
        x: i * (size + 5),
        y: Math.random() * -H,
        v: 0.6 + Math.random() * 1.6,
        len: 3 + ((Math.random() * 7) | 0),
      }));
    };
    setup();
    new ResizeObserver(setup).observe(canvas);
    loopWhileVisible(canvas, () => {
      ctx.clearRect(0, 0, W, H);
      for (const d of drops) {
        d.y += d.v;
        if (d.y - d.len * (size + 3) > H) {
          d.y = -Math.random() * H * 0.5;
          d.v = 0.6 + Math.random() * 1.6;
        }
        for (let k = 0; k < d.len; k++) {
          const y = Math.round((d.y - k * (size + 3)) / (size + 3)) * (size + 3);
          ctx.globalAlpha = (1 - k / d.len) * (k === 0 ? 0.55 : 0.18);
          ctx.fillStyle = k === 0 ? '#eceeea' : '#5bd68f';
          ctx.beginPath();
          tilePath(ctx, d.x, y, size);
          ctx.fill();
        }
      }
      ctx.globalAlpha = 1;
    });
  };

  /* ---------------- Mascot A/B test ----------------
     Each visitor gets a sticky 50/50 variant. It is published as a Shopify customer event and
     added to the contact form as a hidden field, so inquiries arrive tagged with what they saw. */

  const initMascotAB = (screen) => {
    const KEY = 'rt-ab-mascot';
    let variant;
    try {
      variant = localStorage.getItem(KEY);
    } catch (e) {}
    if (variant !== 'a' && variant !== 'b') {
      variant = Math.random() < 0.5 ? 'a' : 'b';
      try {
        localStorage.setItem(KEY, variant);
      } catch (e) {}
    }
    const arts = screen.querySelectorAll('.rt-px--mascot');
    arts.forEach((svg, i) => svg.classList.toggle('is-active', (i === 0 ? 'a' : 'b') === variant));

    document.querySelectorAll('form#ContactForm').forEach((form) => {
      if (form.querySelector('input[name="contact[mascot_variant]"]')) return;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'contact[mascot_variant]';
      input.value = variant.toUpperCase();
      form.append(input);
    });
    try {
      window.Shopify?.analytics?.publish?.('rt_mascot_variant', { variant });
    } catch (e) {}
  };

  /* ---------------- Boot ---------------- */

  const boot = () => {
    initSpotlight();
    initScramble();
    document.querySelectorAll('.rt-terminal').forEach(initTerminal);
    document.querySelectorAll('[data-rt-ab="mascot"]').forEach(initMascotAB);
    const startCanvases = () =>
      onIdle(() => {
        document.querySelectorAll('.rt-hero').forEach(initHero);
        document.querySelectorAll('.rt-cta__rain').forEach(initRain);
      });
    if (document.readyState === 'complete') startCanvases();
    else window.addEventListener('load', startCanvases, { once: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Theme editor: re-init sections when they are re-rendered.
  document.addEventListener('shopify:section:load', (e) => {
    const s = e.target;
    s.querySelectorAll('.rt-hero').forEach(initHero);
    s.querySelectorAll('.rt-terminal').forEach(initTerminal);
    s.querySelectorAll('.rt-cta__rain').forEach(initRain);
  });
})();
